import { useCallback } from "react";
import { useAtomValue } from "jotai";
import {
  getAssetId,
  getTablesOrigin,
  interfaceUrlAndNameAtomFamily_SUSPENDS,
  ZapMetadata,
  getAgentsOrigin,
  AssetType,
} from "@zapier/canvas-state";
import { preloadService } from "../services/PreloadService";

interface UseAssetPreloadOptions {
  assetType: AssetType;
  nodeData?: any;
  nodeId?: string;
  // For interface nodes
  projectId?: string;
  pageId?: string;
  // For zap nodes
  zapId?: string;
  zap?: ZapMetadata;
}

/**
 * Hook that provides a mouse enter handler for preloading asset URLs
 * when focus mode is enabled for better responsiveness.
 */
export function useAssetPreload(options: UseAssetPreloadOptions) {
  const { assetType, nodeData, nodeId, projectId, pageId, zapId } = options;

  // For interface nodes, we need the URL from the async atom
  const interfaceUrlData =
    assetType === "interface" && projectId && pageId
      ? useAtomValue(
          interfaceUrlAndNameAtomFamily_SUSPENDS({
            projectId,
            pageId,
            nodeId: nodeId!,
          }),
        )
      : null;

  const handleMouseEnter = useCallback(() => {
    let urlToPreload: string | null = null;

    switch (assetType) {
      case "table": {
        const tableId = nodeData ? getAssetId(nodeData, "table") : null;
        if (!tableId) return;
        urlToPreload = `https://${getTablesOrigin()}/app/tables/t/${tableId}`;
        break;
      }

      case "interface": {
        const interfaceUrl = interfaceUrlData?.url;
        if (!interfaceUrl) return;
        urlToPreload = interfaceUrl;
        break;
      }

      case "zap": {
        if (!zapId) return;
        urlToPreload = `/editor/${zapId}/draft`;
        break;
      }

      case "chatbot": {
        const chatbotId = nodeData ? getAssetId(nodeData, "chatbot") : null;
        if (!chatbotId) return;
        urlToPreload = `/app/chatbots/${chatbotId}`;
        break;
      }

      case "agent": {
        const agentId = nodeData ? getAssetId(nodeData, "agent") : null;
        if (!agentId) return;
        urlToPreload = `${getAgentsOrigin()}/bots/${agentId}/settings-preview`;
        break;
      }
    }

    if (urlToPreload) {
      preloadService.preload(urlToPreload);
    }
  }, [assetType, nodeData, interfaceUrlData?.url, zapId]);

  return handleMouseEnter;
}

/**
 * Simplified hook for preloading a URL when focus mode is enabled.
 * Use this when you already have the URL to preload.
 */
export function useUrlPreload(assetType: AssetType, url: string | null) {
  const handleMouseEnter = useCallback(() => {
    if (!url) return;
    preloadService.preload(url);
  }, [url]);

  return handleMouseEnter;
}
